import {useCallback} from 'react';

import type {
  IUseLiabilitiesSubStepParams,
  IUseLiabilitiesSubStepResult,
  LiabilityDraft,
} from '../financials.types';
import {
  lshCreateLocalId,
  lshParseCurrency,
  lshSanitizeCurrencyInput,
} from '../financials.utils';

export function lshUseLiabilitiesSubStep({
  liabilityAccountTypes,
  setLiabilityRows,
}: IUseLiabilitiesSubStepParams): IUseLiabilitiesSubStepResult {
  const handleAddLiability = useCallback(() => {
    const first = liabilityAccountTypes[0];
    const draft: LiabilityDraft = {
      localId: lshCreateLocalId(),
      lender: '',
      creditLimitAmount: '',
      currentBalanceLimit: '',
      monthlyInstallmentAmount: '',
      inArrears: false,
      liabilityAccountTypeId: first?.id ? Number(first.id) : undefined,
      liabilityAccountTypeName: first?.name || undefined,
    };
    setLiabilityRows((current) => [...current, draft]);
  }, [liabilityAccountTypes, setLiabilityRows]);

  const handleUpdateLiabilityRowField = useCallback(
    (rowId: number, field: keyof LiabilityDraft, value: string | boolean) => {
      setLiabilityRows((current) =>
        current.map((row) => {
          if (row.localId !== rowId) return row;

          if (field === 'liabilityAccountTypeId' && typeof value === 'string') {
            const id = Number(value);
            const hasValidId =
              value.trim().length > 0 && Number.isFinite(id) && id > 0;
            const option = liabilityAccountTypes.find(
              (item) => Number(item.id) === id,
            );
            return {
              ...row,
              liabilityAccountTypeId: hasValidId ? id : undefined,
              liabilityAccountTypeName: hasValidId
                ? option?.name || undefined
                : undefined,
            };
          }

          if (field === 'inArrears' && typeof value === 'boolean') {
            return {...row, inArrears: value};
          }

          if (
            field === 'creditLimitAmount' ||
            field === 'currentBalanceLimit' ||
            field === 'monthlyInstallmentAmount'
          ) {
            return {
              ...row,
              [field]: lshSanitizeCurrencyInput(String(value)),
            } as LiabilityDraft;
          }

          return {...row, [field]: value} as LiabilityDraft;
        }),
      );
    },
    [liabilityAccountTypes, setLiabilityRows],
  );

  const validateLiabilityRow = useCallback((row: LiabilityDraft) => {
    if (!row.lender.trim()) {
      return 'Enter the lender name.';
    }

    if (lshParseCurrency(row.creditLimitAmount) <= 0) {
      return 'Enter the credit limit amount.';
    }

    if (!row.liabilityAccountTypeId) {
      return 'Select the liability type.';
    }

    return null;
  }, []);

  return {
    handleAddLiability,
    handleUpdateLiabilityRowField,
    validateLiabilityRow,
  };
}
